export const MAX_PROCESS_DRAFT_CHARACTERS = 128 * 1024;

const PROCESS_DRAFT_KEY_PATTERN =
  /^draft-(?:user-\d+|anon)-\d+-step-(?:\d+|unknown)$/;
const GUARD_STATE_KEY = "__bpmProcessDraftStorageGuardState__";

export const isProcessDraftStorageKey = (key) =>
  typeof key === "string" && PROCESS_DRAFT_KEY_PATTERN.test(key);

export const isOversizedProcessDraft = (
  key,
  value,
  maxCharacters = MAX_PROCESS_DRAFT_CHARACTERS,
) =>
  isProcessDraftStorageKey(key) &&
  typeof value === "string" &&
  value.length > maxCharacters;

const getGuardState = (storage) => {
  if (!storage) return null;
  const prototype = Object.getPrototypeOf(storage);
  return prototype?.[GUARD_STATE_KEY] || null;
};

export const cleanOversizedProcessDrafts = (
  storage,
  maxCharacters = MAX_PROCESS_DRAFT_CHARACTERS,
) => {
  if (!storage) return 0;

  const state = getGuardState(storage);
  const getItem = state?.originalGetItem
    ? (key) => state.originalGetItem.call(storage, key)
    : (key) => storage.getItem(key);
  const removeItem = state?.originalRemoveItem
    ? (key) => state.originalRemoveItem.call(storage, key)
    : (key) => storage.removeItem(key);

  const keysToRemove = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!isProcessDraftStorageKey(key)) continue;
      const value = getItem(key);
      if (isOversizedProcessDraft(key, value, maxCharacters)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(removeItem);
  } catch {
    return 0;
  }

  return keysToRemove.length;
};

/**
 * ProcessRunner historically stores the whole form state synchronously in
 * localStorage. Claiming or releasing a task changes the instance object and
 * triggers that persistence even when no form field was edited. Old requests
 * may contain large tables, attachments or data URLs; parsing those drafts on
 * the next opening can block the browser main thread.
 *
 * This guard is deliberately scoped to ProcessRunner draft keys. Normal small
 * drafts keep working, while oversized drafts are discarded before JSON.parse
 * and are not persisted again.
 */
export const installProcessDraftStorageGuard = (
  storage = typeof window !== "undefined" ? window.localStorage : null,
  maxCharacters = MAX_PROCESS_DRAFT_CHARACTERS,
) => {
  if (!storage) return { cleanupCount: 0, installed: false };

  const prototype = Object.getPrototypeOf(storage);
  if (!prototype) return { cleanupCount: 0, installed: false };

  let state = prototype[GUARD_STATE_KEY];
  if (!state) {
    state = {
      guardedStorages: new WeakMap(),
      originalGetItem: prototype.getItem,
      originalSetItem: prototype.setItem,
      originalRemoveItem: prototype.removeItem,
    };

    try {
      Object.defineProperty(prototype, GUARD_STATE_KEY, {
        configurable: false,
        enumerable: false,
        writable: false,
        value: state,
      });

      prototype.getItem = function guardedGetItem(key) {
        const value = state.originalGetItem.call(this, key);
        const limit = state.guardedStorages.get(this);
        if (
          typeof limit === "number" &&
          isOversizedProcessDraft(key, value, limit)
        ) {
          state.originalRemoveItem.call(this, key);
          return null;
        }
        return value;
      };

      prototype.setItem = function guardedSetItem(key, value) {
        const limit = state.guardedStorages.get(this);
        if (
          typeof limit === "number" &&
          isOversizedProcessDraft(key, value, limit)
        ) {
          state.originalRemoveItem.call(this, key);
          return undefined;
        }
        return state.originalSetItem.call(this, key, value);
      };
    } catch {
      return {
        cleanupCount: cleanOversizedProcessDrafts(storage, maxCharacters),
        installed: false,
      };
    }
  }

  state.guardedStorages.set(storage, maxCharacters);
  const cleanupCount = cleanOversizedProcessDrafts(storage, maxCharacters);
  return { cleanupCount, installed: true };
};
